import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { omit } from 'lodash'
import { ref } from 'vue'
import { ToastInterface, useToast } from 'vue-toastification'

import { Hotel } from '../interfaces/Hotel'

interface Props {
	immediate?: boolean
	params?: Params
}

interface CreateParams {
	name: string
	brandId?: number
	rating: number
	groupId?: number
	vat?: number
	ish?: number
}

interface UpdateParams {
	id: number
	name?: string
	brandId?: number | null
	rating?: number
	groupId?: number | null
	stateId?: number
	vat?: number
	ish?: number
	city?: string
	address?: string
	country?: string
	latitude?: number
	longitude?: number
}

interface Params {
	id: number
}

interface DeleteParams {
	id: number
	commentary: string
}

interface ChangeStateParams extends Params {
	stateId: number
	comment: string
}

const getHotel = async (params: Params) => {
	const { data } = await axios.get<Hotel>(`/api/internal/hotels/${params.id}`)

	return data
}

const createHotel = async (hotel: CreateParams, toast: ToastInterface) => {
	try {
		const { data } = await axios.post<Hotel>('/api/internal/hotels', hotel)

		toast.success('Hotel creado correctamente')

		return data
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const updateHotel = async (params: UpdateParams, toast: ToastInterface) => {
	try {
		const { data } = await axios.put<Hotel>(
			`/api/internal/hotels/${params.id}`,
			omit(params, ['id'])
		)

		toast.success('Hotel actualizado correctamente')

		return data
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const changeStateHotel = async (
	params: ChangeStateParams,
	toast: ToastInterface
) => {
	try {
		const { data } = await axios.put<Hotel>(
			`/api/internal/hotels/${params.id}/change-state`,
			{
				stateId: params.stateId,
				comment: params.comment,
			}
		)

		toast.success('Estado del hotel actualizado correctamente')

		return data
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const deleteHotel = async (params: DeleteParams, toast: ToastInterface) => {
	try {
		await axios.delete(`/api/internal/hotels/${params.id}`, {
			data: {
				commentary: params.commentary,
			},
		})

		toast.success('Hotel eliminado correctamente')
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const restoreHotel = async (params: Params, toast: ToastInterface) => {
	try {
		const { data } = await axios.post<Hotel>(
			`/api/internal/hotels/${params.id}/restore`
		)

		toast.success('Hotel restaurado correctamente')

		return data
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const useHotels = (props: Props) => {
	const { immediate = true } = props
	const hotelId = ref<number | null>(props.params?.id || null)
	const toast = useToast()

	const { data, isLoading, isRefetching, refetch } = useQuery(
		['hotel', props.params?.id || hotelId.value!],
		() =>
			getHotel({
				id: props.params?.id! || hotelId.value!,
			}),
		{
			enabled: immediate,
		}
	)

	const createHotelMutation = useMutation((hotel: CreateParams) =>
		createHotel(hotel, toast)
	)

	const updateHotelMutation = useMutation((hotel: UpdateParams) =>
		updateHotel(hotel, toast)
	)

	const changeStateHotelMutation = useMutation((params: ChangeStateParams) =>
		changeStateHotel(params, toast)
	)

	const deleteHotelMutation = useMutation((params: DeleteParams) =>
		deleteHotel(params, toast)
	)

	const restoreHotelMutation = useMutation((params: Params) =>
		restoreHotel(params, toast)
	)

	return {
		hotelId,
		data,
		isLoading,
		refetch,
		isRefetching,

		createHotelMutation: createHotelMutation.mutate,
		createHotelAsyncMutation: createHotelMutation.mutateAsync,
		createHotelLoading: createHotelMutation.isLoading,
		createHotelError: createHotelMutation.error,

		updateHotelMutation: updateHotelMutation.mutate,
		updateHotelAsyncMutation: updateHotelMutation.mutateAsync,
		updateHotelLoading: updateHotelMutation.isLoading,
		updateHotelError: updateHotelMutation.error,

		changeStateHotelMutation: changeStateHotelMutation.mutate,
		changeStateHotelAsyncMutation: changeStateHotelMutation.mutateAsync,
		changeStateHotelLoading: changeStateHotelMutation.isLoading,
		changeStateHotelError: changeStateHotelMutation.error,

		deleteHotelMutation: deleteHotelMutation.mutate,
		deleteHotelAsyncMutation: deleteHotelMutation.mutateAsync,
		deleteHotelLoading: deleteHotelMutation.isLoading,
		deleteHotelError: deleteHotelMutation.error,

		restoreHotelMutation: restoreHotelMutation.mutate,
		restoreHotelAsyncMutation: restoreHotelMutation.mutateAsync,
		restoreHotelLoading: restoreHotelMutation.isLoading,
		restoreHotelError: restoreHotelMutation.error,
	}
}

export default useHotels
